import {
  Box,
  Button,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Spinner,
  TabsProps as ChakraTabsProps,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOrganization, fetchSubmissionContext } from 'src/api/queries';
import { Overlay } from 'src/components/Overlay';
import {
  IJournalEmailsConfig,
  IJournalPublishingModel,
  IPeerReviewModelConfig
} from '@publishing-engine/domain/models';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { ButtonText, Tabs } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NavbarDashboard from 'src/components/NavbarDashboard';
import { useCallback, useMemo, useState } from 'react';

import GeneralSettingsTab from './tabs/GeneralSettingsTab';
import EditorialWorkflowTab from './tabs/EditorialWorkflowTab';
import TransferSettingsTab from './tabs/TransferSettingsTab';
import ArticleTypesTab from './tabs/ArticleTypesTab';
import Footer from 'src/components/Footer';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import ActivateDeactivateJournalModal, {
  ActivateDeactivateJournalButton
} from 'src/components/ActivateDeactivateJournalModal';
import ReviewerSearchTab from './tabs/ReviewerSearchTab';
import { getArticleWorkflowIdsMissingPeerReviewModel } from './GeneralInfo/peerReviewModelConfig';
import { COCRecommendationLockReason } from './CocConfiguration/types';
import IntegrityChecksTab from './tabs/IntegrityChecksTab';
import WorkflowBucketsTab from './tabs/WorkFlowBucketsTab';
import CheckListsTab from './tabs/CheckListsTab';
import FeatureFlagsTab from './tabs/FeatureFlagsTab';

export type IJournalSettingFormData = {
  data?: {
    name?: string;
    description?: string;
    introduction?: string;
  };
  config?: {
    email?: string;
    url?: string;
    issn?: {
      print?: string;
      online?: string;
    };
    publishingModel?: IJournalPublishingModel;
    peerReviewModel?: IPeerReviewModelConfig[];
    /**
     * Sent as the journal's whole email config — the settings endpoint replaces
     * the object rather than merging it.
     */
    journalEmails?: IJournalEmailsConfig;
    editorialWithdrawalOptions?: string[];
    authorWithdrawalOptions?: string[];
    transferOptions?: string[];
    preRejectionOptions?: string[];
    postRejectionOptions?: string[];
    revisionSeverityOptions?: string[];
    reviewerSearch?: boolean;
    globalLexiconConfig?: boolean;
    lexiconClassId?: string;
    lexiconProjectId?: string;
    boardMemberReviewerAssigment?: boolean;
    isUADEnable?: boolean;
    enableConsolidatedPdf?: boolean;
    assignReviewers?: boolean;
    isCOCRecommendationRequired?: boolean;
    isCoReviewEnabled?: boolean;
    notifyInvitingEditorsOnReviewTargetMet?: boolean;
    logoUrl?: string;
    cms?: {
      lexicon?: {
        reviewerActionCard?: {
          createAccount?: boolean;
        };
      };
    };
  };
};

const ConfigureJournal = () => {
  const { shortname = '', journalId = '' } = useParams();
  const location = useLocation();
  const routeState = (location.state as Record<string, unknown> | null) ?? {};
  const backTab =
    typeof routeState.backTab === 'string' ? routeState.backTab : 'active';
  const backPage =
    typeof routeState.backPage === 'number' ? routeState.backPage : undefined;
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: journal, isLoading } = useQuery({
    queryKey: ['submission-context', journalId],
    queryFn: () => (journalId ? fetchSubmissionContext(journalId) : null),
    retry: 0
  });

  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => (shortname ? fetchOrganization(shortname) : null),
    retry: 0
  });

  const cocConfigEmpty = !(journal as any)?.config?.cocConfig?.length;

  /**
   * A journal goes live with a peer review model for every one of its article
   * types, so an article type without one holds activation back until it is set
   * on the Article Types tab.
   */
  const articleTypesMissingPeerReviewModel = journal
    ? getArticleWorkflowIdsMissingPeerReviewModel(
        journal,
        Object.values(journal.articleWorkflows ?? {})
      )
    : [];

  /**
   * canActivate is derived from live journal data (cocConfig presence) so that
   * it updates reactively when the user adds/edits role assignments on this page.
   * Only meaningful for inactive journals (backTab === 'inactive') — active
   * journals should never show an Activate button.
   * canDeactivate still comes from route state — the single-journal endpoint
   * does not return submission/draft counts needed to compute it.
   */
  const canActivate =
    backTab === 'inactive' &&
    !cocConfigEmpty &&
    articleTypesMissingPeerReviewModel.length === 0;
  const canDeactivate = routeState.canDeactivate === true;

  /**
   * The "Enable decision Recommendations" toggle is frozen once the journal is
   * live, because submissions in flight already carry the recommendation state.
   * Both signals arrive in route state from the journals list — the
   * single-journal endpoint returns neither, same as canDeactivate above.
   */
  const submissionCount =
    typeof routeState.submissionCount === 'number'
      ? routeState.submissionCount
      : 0;
  const hasSubmissions = submissionCount > 0;
  const isJournalActive = backTab === 'active';
  /** Either condition means saved role max limits may only be raised. */
  const isJournalLocked = hasSubmissions || isJournalActive;
  /**
   * An active journal can also hold submissions, so submissions win the message
   * as the more specific blocker to clear.
   */
  const recommendationLockReason: COCRecommendationLockReason | undefined =
    hasSubmissions
      ? 'hasSubmissions'
      : isJournalActive
      ? 'journalActive'
      : undefined;
  const toggleAction: 'activate' | 'deactivate' = canActivate
    ? 'activate'
    : canDeactivate
    ? 'deactivate'
    : backTab === 'inactive'
    ? 'activate'
    : 'deactivate';

  const chakraTabsProps = useMemo(
    () =>
      ({
        index: activeTabIndex,
        onChange: (index: number) => setActiveTabIndex(index)
      } as ChakraTabsProps),
    [activeTabIndex]
  );

  const tabLabels = useMemo(
    () => [
      'General',
      'Assignments',
      'Article Types',
      'Integrity',
      'Transfer',

      'Peer Review',
      'Workflow Buckets',
      'Checklists',
      'Feature Flags'
    ],
    []
  );

  const renderTabContent = () => {
    if (!journal) return null;
    switch (activeTabIndex) {
      case 0:
        return (
          <GeneralSettingsTab
            journal={journal}
            guidelineLink={journal?.data?.guidelineLink}
            isActive={
              backTab === 'active'
                ? true
                : backTab === 'inactive'
                ? false
                : undefined
            }
            journalId={journalId}
          />
        );

      case 1:
        return (
          <EditorialWorkflowTab
            journalId={journalId}
            workspaceId={journal.workspaceId}
            journalName={journal.data.name}
            cocConfig={journal?.config.cocConfig}
            isCOCRecommendationRequired={
              journal?.config.isCOCRecommendationRequired
            }
            recommendationLockReason={recommendationLockReason}
            isJournalLocked={isJournalLocked}
          />
        );
      case 2:
        return (
          <ArticleTypesTab
            orgShortname={organization?.shortname!}
            journalId={journalId}
          />
        );
      case 3:
        return <IntegrityChecksTab journalId={journalId} />;
      case 4:
        return <TransferSettingsTab />;
      case 5:
        return (
          <ReviewerSearchTab
            journalId={journalId}
            reviewerSearch={journal?.config.reviewerSearch}
            createAccount={
              journal?.config.cms?.lexicon?.reviewerActionCard?.createAccount
            }
            lexiconClassId={journal?.config.lexiconClassId}
            lexiconProjectId={journal?.config.lexiconProjectId}
            globalReviewerSearchEnabled={journal?.config.globalLexiconConfig}
            isBoardMemberReviewerAssignmentEnabled={
              journal?.config.boardMemberReviewerAssigment
            }
            journalName={journal.data.name}
            organizationId={journal.organizationId}
          />
        );
      case 6:
        return (
          <WorkflowBucketsTab
            journalId={journalId}
            journalName={journal.data.name}
            cocConfig={journal?.config.cocConfig}
          />
        );
      case 7:
        return (
          <CheckListsTab
            journalId={journalId}
            journalName={journal.data.name}
            cocConfig={journal?.config.cocConfig}
          />
        );
      case 8:
        return (
          <FeatureFlagsTab
            journalId={journalId}
            journalName={journal.data.name}
            isUADEnable={journal?.config.isUADEnable}
            enableConsolidatedPdf={journal?.config.enableConsolidatedPdf}
            notifyInvitingEditorsOnReviewTargetMet={
              journal?.config.notifyInvitingEditorsOnReviewTargetMet
            }
          />
        );
      default:
        return null;
    }
  };

  const handleToggleStatusSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['submission-context', journalId]
    });
    navigate(`/app/workspace/${shortname}/manage/journals`, {
      state: {
        backTab,
        backPage
      }
    });
  }, [
    queryClient,
    journalId,
    navigate,
    shortname,
    backTab,
    backPage
  ]);

  if (isLoading) {
    return (
      <Center height="100vh">
        <Spinner color="actionPrimaryFill" thickness="3px" />
      </Center>
    );
  }

  if (!journal) return <Text>Journal not found</Text>;

  return (
    <Overlay
      backgroundColor="surfaceSecondaryFill"
      id="scrollable-wrapper"
      display="flex"
      flexDirection="column"
      minHeight="100vh"
    >
      <NavbarDashboard />
      <Box padding={{ base: 2, md: 2 }} flex="1">
        <Flex flexDirection={'column'} gap={4}>
          <Flex flexDirection={'column'} gap={4}>
            <Link
              to={`/app/workspace/${shortname}/manage/journals`}
              state={{ backTab, backPage }}
            >
              <ButtonText
                iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
                marginLeft={-1}
              >
                Back
              </ButtonText>
            </Link>
            <Flex
              alignItems="flex-start"
              gap={4}
              width="100%"
              justifyContent="space-between"
              flexWrap="wrap"
              marginBottom={4}
            >
              <Text textStyle={'headingTitle'} wordBreak="break-word">
                {journal.data.name}
              </Text>
              <Box marginStart="auto">
                <ActivateDeactivateJournalButton
                  journal={journal}
                  onClick={onConfirmOpen}
                  action={toggleAction}
                  canActivate={canActivate}
                  canDeactivate={canDeactivate}
                  cocConfigEmpty={cocConfigEmpty}
                  missingPeerReviewModel={
                    articleTypesMissingPeerReviewModel.length > 0
                  }
                />
              </Box>
            </Flex>
          </Flex>

          {/* Mobile and tablet: dropdown tab selector */}
          <Box display={{ base: 'block', xl: 'none' }}>
            <Menu>
              <MenuButton
                borderWidth="0"
                borderRadius="0"
                variant="outline"
                as={Button}
                width="100%"
                justifyContent="space-between"
                textAlign="left"
                bg="white"
                border="1px solid"
                borderColor="neutralPrimaryBorder"
                rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                _hover={{ bg: 'white' }}
                _active={{ bg: 'white' }}
              >
                {tabLabels[activeTabIndex]}
              </MenuButton>
              <MenuList width="100%">
                <MenuGroup title="Configure Journal">
                  {tabLabels.map((label, index) => (
                    <MenuItem
                      key={label}
                      onClick={() => setActiveTabIndex(index)}
                      color={
                        activeTabIndex === index
                          ? 'actionPrimaryFill'
                          : undefined
                      }
                      fontWeight={
                        activeTabIndex === index ? 'semibold' : undefined
                      }
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuGroup>
              </MenuList>
            </Menu>
          </Box>

          {/* Desktop: tabs */}
          <Box display={{ base: 'none', xl: 'block' }}>
            <Tabs
              chakraTabsProps={chakraTabsProps}
              tabs={tabLabels.map(label => ({ label, content: null }))}
            />
          </Box>

          {/* Active tab content */}
          <Flex mb={6} paddingLeft={2}>
            {renderTabContent()}
          </Flex>
        </Flex>
      </Box>
      <Footer />
      <ScrollToTopButton />
      <ActivateDeactivateJournalModal
        journal={journal}
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        onSuccess={handleToggleStatusSuccess}
        action={toggleAction}
      />
    </Overlay>
  );
};

export default ConfigureJournal;
